import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as TreeIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { CostCenterTreeDto } from '../../types/costCenters.types';

interface CostCenterTreeProps {
  data: CostCenterTreeDto[];
  onEdit: (costCenter: any) => void;
  onDelete: (costCenter: any) => void;
}

const CostCenterTree: React.FC<CostCenterTreeProps> = ({
  data,
  onEdit,
  onDelete
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const renderNode = (node: CostCenterTreeDto, level: number = 0) => {
    return (
      <Box key={node.costCenterId} sx={{ mb: 1 }}>
        {/* Current Node */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            ml: level * 4,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: level === 0 ? 'action.hover' : 'background.paper',
            '&:hover': {
              backgroundColor: 'action.selected'
            }
          }}
        >
          {/* Tree Icon */}
          {level > 0 && (
            <TreeIcon 
              sx={{ 
                color: 'text.secondary',
                fontSize: 20 
              }} 
            />
          )}

          {/* Code */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: level === 0 ? 'bold' : 'normal',
              minWidth: 100,
              fontFamily: 'monospace'
            }}
          >
            {node.costCenterCode}
          </Typography>

          {/* Name */}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontWeight: level === 0 ? 'bold' : 'normal'
            }}
          >
            {isRTL ? node.costCenterNameAr : node.costCenterName}
          </Typography>

          {/* Status Chips */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={node.isFinalCostCenter 
                ? (isRTL ? 'نهائي' : 'Final')
                : (isRTL ? 'غير نهائي' : 'Non-Final')
              }
              size="small"
              color={node.isFinalCostCenter ? 'success' : 'default'}
              variant="outlined"
            />
            <Chip
              label={node.isActive 
                ? (isRTL ? 'نشط' : 'Active')
                : (isRTL ? 'غير نشط' : 'Inactive')
              }
              size="small"
              color={node.isActive ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
              <IconButton
                size="small"
                onClick={() => onEdit(node)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
              <IconButton
                size="small"
                onClick={() => onDelete(node)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Children Nodes */}
        {node.children && node.children.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {node.children.map((child) => renderNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {data.map((node) => renderNode(node, 0))}
    </Box>
  );
};

export default CostCenterTree;